import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { trackPageView } from "@/lib/analytics";

function useScrollRestoration() {
  const [location] = useLocation();
  const prevPathRef = useRef<string>("");
  
  useEffect(() => {
    const currentPath = location.split("#")[0];
    const prevPath = prevPathRef.current;
    const hasHash = window.location.hash.length > 0;
    
    if (currentPath !== prevPath && !hasHash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    
    prevPathRef.current = currentPath;
  }, [location]);
}
import { ProtectedRoute } from "@/components/protected-route";

import Home from "@/pages/home";
import NotFound from "@/pages/not-found";

const Careers = lazy(() => import("@/pages/careers"));
const Services = lazy(() => import("@/pages/services"));
const Fleet = lazy(() => import("@/pages/fleet"));
const About = lazy(() => import("@/pages/about"));
const Contact = lazy(() => import("@/pages/contact"));
const Blog = lazy(() => import("@/pages/blog"));
const BlogPost = lazy(() => import("@/pages/blog-post"));
const ThankYou = lazy(() => import("@/pages/thank-you"));
const Accessibility = lazy(() => import("@/pages/accessibility"));
const Terms = lazy(() => import("@/pages/terms"));
const Privacy = lazy(() => import("@/pages/privacy"));

const AdminLogin = lazy(() => import("@/pages/admin-login"));
const AdminDashboard = lazy(() => import("@/pages/admin-dashboard"));
const AdminTestimonials = lazy(() => import("@/pages/admin-testimonials"));
const AdminBlog = lazy(() => import("@/pages/admin-blog"));
const AdminPages = lazy(() => import("@/pages/admin-pages"));
const AdminApplications = lazy(() => import("@/pages/admin-applications"));
const AdminApplicationDetail = lazy(() => import("@/pages/admin-application-detail"));
const AdminMedia = lazy(() => import("@/pages/admin-media"));
const AdminFormFiles = lazy(() => import("@/pages/admin-form-files"));
const AdminAIGenerator = lazy(() => import("@/pages/admin-ai-generator"));
const AdminTopicBucket = lazy(() => import("@/pages/admin-topic-bucket"));
const AdminArticleQueue = lazy(() => import("@/pages/admin-article-queue"));
const AdminAnalytics = lazy(() => import("@/pages/admin-analytics"));
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const WelcomePage = lazy(() => import("@/pages/welcome"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

function ApplyRedirect() {
  const [, setLocation] = useLocation();
  
  useEffect(() => {
    setLocation("/careers#application");
  }, [setLocation]);
  
  return null;
}

function Router() {
  const [location] = useLocation();
  
  useScrollRestoration();

  useEffect(() => {
    trackPageView(location);
  }, [location]);

  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/careers">
        <Suspense fallback={<PageLoader />}><Careers /></Suspense>
      </Route>
      <Route path="/apply" component={ApplyRedirect} />
      <Route path="/services">
        <Suspense fallback={<PageLoader />}><Services /></Suspense>
      </Route>
      <Route path="/fleet">
        <Suspense fallback={<PageLoader />}><Fleet /></Suspense>
      </Route>
      <Route path="/about">
        <Suspense fallback={<PageLoader />}><About /></Suspense>
      </Route>
      <Route path="/contact">
        <Suspense fallback={<PageLoader />}><Contact /></Suspense>
      </Route>
      <Route path="/blog">
        <Suspense fallback={<PageLoader />}><Blog /></Suspense>
      </Route>
      <Route path="/blog/:slug">
        <Suspense fallback={<PageLoader />}><BlogPost /></Suspense>
      </Route>
      <Route path="/admin/login">
        <Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>
      </Route>
      <Route path="/admin">
        <Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>
      </Route>
      <Route path="/admin/testimonials">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminTestimonials /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/blog">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminBlog /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/pages">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminPages /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/applications">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminApplications /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/applications/:id">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminApplicationDetail /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/media">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminMedia /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/form-files">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminFormFiles /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/ai-generator">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminAIGenerator /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/topic-bucket">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminTopicBucket /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/article-queue">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminArticleQueue /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/analytics">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminAnalytics /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/admin/users">
        <ProtectedRoute><Suspense fallback={<PageLoader />}><AdminUsers /></Suspense></ProtectedRoute>
      </Route>
      <Route path="/thank-you">
        <Suspense fallback={<PageLoader />}><ThankYou /></Suspense>
      </Route>
      <Route path="/welcome">
        <Suspense fallback={<PageLoader />}><WelcomePage /></Suspense>
      </Route>
      <Route path="/accessibility">
        <Suspense fallback={<PageLoader />}><Accessibility /></Suspense>
      </Route>
      <Route path="/terms">
        <Suspense fallback={<PageLoader />}><Terms /></Suspense>
      </Route>
      <Route path="/privacy">
        <Suspense fallback={<PageLoader />}><Privacy /></Suspense>
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Router />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
